import { useState, useRef, useCallback, useEffect } from 'react';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import { ChevronDown, Upload, X, Building2, CheckCircle } from 'lucide-react';
import { compressImage } from '../utils/imageCompression';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const USER_TYPES = [
  { value: 'volontario', label: 'Volontario' },
  { value: 'peschereccio', label: 'Peschereccio (Fishing for Litter)' },
  { value: 'azienda', label: 'Azienda' },
  { value: 'ente', label: 'Ente / Comune' },
  { value: 'associazione', label: 'Associazione' },
  { value: 'fondazione', label: 'Fondazione' }
];

const VolunteerNameModal = ({ onSave }) => {
  const { language } = useLanguage();
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('volontario');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const logoInputRef = useRef(null);
  
  // Codice invito organizzazione
  const [inviteCode, setInviteCode] = useState('');
  const [inviteOrg, setInviteOrg] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  const needsOrganization = userType !== 'volontario';

  // Check for pending invite code from URL
  useEffect(() => {
    const pendingCode = localStorage.getItem('pending_invite_code');
    if (pendingCode) {
      setInviteCode(pendingCode);
      validateInviteCode(pendingCode);
    }
  }, []);

  // Validate invite code
  const validateInviteCode = async (code) => {
    if (!code || code.length < 3) {
      setInviteOrg(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await axios.get(`${API_URL}/api/organizations/validate/${code}`);
      if (response.data.valid) {
        setInviteOrg(response.data);
        toast.success(`Invito da ${response.data.organization_name} verificato!`);
      }
    } catch (error) {
      setInviteOrg(null);
    } finally {
      setValidatingCode(false);
    }
  };

  // Handle invite code change
  const handleInviteCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setInviteCode(code);
    if (code.length >= 3) {
      validateInviteCode(code);
    } else {
      setInviteOrg(null);
    }
  };

  const handleLogoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setCompressing(true);
    try {
      const compressed = await compressImage(file, 256, 0.7);
      setOrganizationLogo(compressed.base64);
      setLogoPreview(compressed.base64);
    } catch (error) {
      console.error('Compression error:', error);
      toast.error('Errore compressione logo');
    } finally {
      setCompressing(false);
    }
  }, []);

  const removeLogo = useCallback(() => {
    setOrganizationLogo(null);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  }, []);

  // Tipi che richiedono codice invito obbligatorio
  const requiresInviteCode = ['azienda', 'ente', 'associazione', 'fondazione'].includes(userType);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Inserisci il tuo nome');
      return;
    }
    
    // Verifica codice invito obbligatorio per aziende/enti/associazioni
    if (requiresInviteCode && !inviteOrg) {
      toast.error('Per registrarti come organizzazione devi inserire un codice invito valido. Contatta Respiro Blu per ottenerlo.');
      return;
    }
    
    // Rimuovi il codice invito pendente
    localStorage.removeItem('pending_invite_code');
    
    onSave({
      name: name.trim(),
      user_type: userType,
      organization_name: needsOrganization ? organizationName.trim() : '',
      organization_logo: needsOrganization ? organizationLogo : null,
      invite_code: inviteOrg ? inviteCode : null
    });
  };

  const getOrganizationPlaceholder = () => {
    switch (userType) {
      case 'peschereccio': return 'Nome del peschereccio';
      case 'azienda': return 'Nome dell\'azienda';
      case 'ente': return 'Nome dell\'ente o comune';
      case 'associazione': return 'Nome dell\'associazione';
      case 'fondazione': return 'Nome della fondazione';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-cyan-500/30 rounded-2xl max-w-md w-full my-4">
        <div className="p-6">
          <div className="text-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/likcbe5b_IMG_6596.jpeg" 
              alt="Respiro Blu" 
              className="w-20 h-20 mx-auto rounded-xl mb-3 object-cover"
            />
            <h2 className="text-2xl font-bold text-white mb-1" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>BENVENUTO!</h2>
            <p className="text-slate-400 text-sm">Completa il tuo profilo per iniziare</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tipo utente */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">Chi sei?</label>
              <div className="relative">
                <select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 appearance-none cursor-pointer"
                >
                  {USER_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Nome */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">Il tuo nome</label>
              <input
                data-testid="volunteer-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Come ti chiami?"
                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                autoFocus
                required
              />
            </div>

            {/* Nome organizzazione (se non è volontario singolo) */}
            {needsOrganization && (
              <>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                    {userType === 'peschereccio' ? 'Nome Peschereccio' : 'Nome Organizzazione'}
                    <span className="text-slate-500 normal-case ml-1">(opzionale)</span>
                  </label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder={getOrganizationPlaceholder()}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>

                {/* Logo organizzazione */}
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                    Logo <span className="text-slate-500 normal-case">(opzionale)</span>
                  </label>
                  
                  {logoPreview ? (
                    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                      <img src={logoPreview} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
                      <span className="text-slate-300 text-sm flex-1">Logo caricato</span>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={compressing}
                      className="w-full py-3 border-2 border-dashed border-slate-700 rounded-lg text-slate-400 hover:border-cyan-500 hover:text-cyan-400 transition-all flex items-center justify-center gap-2"
                    >
                      {compressing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                          <span>Caricamento...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>Carica logo</span>
                        </>
                      )}
                    </button>
                  )}
                  <input 
                    ref={logoInputRef} 
                    type="file" 
                    accept="image/*" 
                    onChange={handleLogoSelect} 
                    className="hidden" 
                  />
                </div>
              </>
            )}

            {/* Codice Invito Organizzazione */}
            <div className={`pt-2 border-t ${requiresInviteCode ? 'border-amber-500/50' : 'border-slate-800'}`}>
              <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">
                Codice invito {requiresInviteCode ? (
                  <span className="text-amber-400 normal-case ml-1">* obbligatorio per organizzazioni</span>
                ) : (
                  <span className="text-slate-500 normal-case">(opzionale)</span>
                )}
              </label>
              
              {requiresInviteCode && !inviteOrg && (
                <p className="text-amber-400/80 text-xs mb-2">
                  Per registrarti come {userType} devi avere un codice. Contatta Respiro Blu per ottenerlo.
                </p>
              )}
              
              <div className="relative">
                <input
                  type="text"
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  placeholder="es. AZIENDA123"
                  className={`w-full px-4 py-3 bg-slate-800/50 border rounded-lg text-white focus:outline-none uppercase ${
                    inviteOrg ? 'border-emerald-500' : requiresInviteCode ? 'border-amber-500/50 focus:border-amber-500' : 'border-slate-700 focus:border-cyan-500'
                  }`}
                />
                {validatingCode && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {inviteOrg && (
                  <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                )}
              </div>
              
              {/* Info organizzazione invitante */}
              {inviteOrg && (
                <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-3">
                  {inviteOrg.organization_logo ? (
                    <img 
                      src={inviteOrg.organization_logo} 
                      alt="" 
                      className="w-10 h-10 rounded-lg object-contain bg-white p-1"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-emerald-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-emerald-400 font-medium text-sm">{inviteOrg.organization_name}</p>
                    <p className="text-emerald-400/60 text-xs">Entrerai nella loro community!</p>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-all shadow-[0_0_15px_rgba(6,182,212,0.3)] mt-2"
            >
              Inizia
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VolunteerNameModal;
